import { useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'

/**
 * First-run walkthrough (v0.13.0) — dependency-free spotlight tour.
 * Steps target elements by [data-tour] attribute; the spotlight is a fixed div
 * whose giant box-shadow dims everything else. Skippable at every step (Esc too),
 * resumable later from the Fieldbook. Completion persists in localStorage.
 */

export interface TourStep {
  target?: string      // data-tour value; omit for a centered card
  title: string
  body: string
}

export const TOUR_DONE_KEY = 'fa_tour_done'

export const DEFAULT_STEPS: TourStep[] = [
  { title: 'Welcome to Family Ancestry ✨', body: 'Your family becomes a living constellation — people are stars, bonds are the lines between them. This 60-second tour shows you around. You can skip anytime and re-take it from the Fieldbook.' },
  { target: 'nav-trees', title: 'My Trees 🌳', body: 'Your constellations live here. Open one to see the graph, add relatives with + Parent / + Spouse / + Child / + Sibling, or 🔗 link people who are already in the tree.' },
  { target: 'sage', title: 'Sage, your AI genealogist ✳', body: 'Sage doesn’t just answer — it acts. Say “add my grandfather Henry, born 1928 in Boston” and he appears in the tree, bonds included. Voice works too. You start with 100 free credits.' },
  { target: 'search', title: 'Find anyone instantly 🔍', body: 'Press ⌘K (or tap here) to search every person, tree, and gathering you can see — and jump straight to them.' },
  { target: 'veil', title: 'The Privacy Veil 🎭', body: 'One tap masks every real name into initials or aliases — for screen-shares, cafés, and curious shoulders. Tap again to unveil.' },
  { target: 'findplace', title: 'Find your place 🧭', body: 'Tell us your parents’ names and we’ll look for where you fit — only inside trees already shared with you. The tree keepers approve every match.' },
  { target: 'guide', title: 'The Fieldbook 📖', body: 'Step-by-step help for everything — building trees, circles, Sage’s limits and credits, privacy tools. It’s always one click away, right here in the footer.' },
]

export default function Tour({ steps = DEFAULT_STEPS, onClose }: { steps?: TourStep[]; onClose: () => void }) {
  const [i, setI] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const step = steps[i]
  const isMobile = useMemo(() => window.innerWidth < 768, [])

  useEffect(() => {
    const find = () => {
      if (!step.target) { setRect(null); return }
      const el = document.querySelector(`[data-tour="${step.target}"]`)
      if (!el) { setRect(null); return }
      el.scrollIntoView({ block: 'nearest' })
      setRect(el.getBoundingClientRect())
    }
    find()
    window.addEventListener('resize', find)
    return () => window.removeEventListener('resize', find)
  }, [step])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish()
      if (e.key === 'ArrowRight' || e.key === 'Enter') next()
      if (e.key === 'ArrowLeft') setI(v => Math.max(0, v - 1))
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }) // eslint-disable-line react-hooks/exhaustive-deps

  const finish = () => {
    localStorage.setItem(TOUR_DONE_KEY, '1')          // fast path for this browser
    api.updatePrefs({ tour_done: true }).catch(() => {}) // durable across devices
    onClose()
  }
  const next = () => (i >= steps.length - 1 ? finish() : setI(i + 1))

  // spotlight geometry (pad the target a little)
  const spot = rect ? {
    left: Math.max(4, rect.left - 6), top: Math.max(4, rect.top - 6),
    width: rect.width + 12, height: rect.height + 12,
  } : null

  // card position: under the target if it fits, else above; centered when no target; bottom sheet on mobile
  const cardStyle: React.CSSProperties = isMobile || !spot
    ? { left: '50%', transform: 'translateX(-50%)', ...(isMobile ? { bottom: 16 } : { top: '38%' }), position: 'fixed', width: 'min(92vw, 380px)' }
    : (() => {
        const below = spot.top + spot.height + 190 < window.innerHeight
        const left = Math.min(Math.max(12, spot.left), window.innerWidth - 392)
        return { position: 'fixed', left, ...(below ? { top: spot.top + spot.height + 14 } : { bottom: window.innerHeight - spot.top + 14 }), width: 380 } as React.CSSProperties
      })()

  return (
    <div className="fixed inset-0 z-[90]" role="dialog" aria-label="Product tour">
      {/* dim layer: spotlight cutout via huge box-shadow, or full dim when no target */}
      {spot ? (
        <div className="fixed rounded-xl pointer-events-none transition-all duration-300"
          style={{ ...spot, boxShadow: '0 0 0 9999px rgba(8,5,20,0.82)', border: '2px solid rgba(232,188,106,0.75)' }} />
      ) : (
        <div className="fixed inset-0 bg-void-950/85" />
      )}

      <div className="card-glass !bg-void-900/95 p-5 agent-enter" style={cardStyle}>
        <p className="text-[10px] uppercase tracking-wider text-gold-400/80 mb-1">{i + 1} of {steps.length}</p>
        <h3 className="font-display text-lg font-bold text-slate-100 mb-1.5">{step.title}</h3>
        <p className="text-sm text-slate-400 leading-relaxed mb-4">{step.body}</p>
        <div className="flex items-center justify-between">
          <button onClick={finish} className="text-xs text-slate-500 hover:text-slate-300">Skip tour</button>
          <div className="flex gap-2">
            {i > 0 && <button onClick={() => setI(i - 1)} className="btn-ghost !py-1.5 !px-3 text-xs">Back</button>}
            <button onClick={next} className="btn-gold !py-1.5 !px-4 text-xs">{i >= steps.length - 1 ? 'Finish ✨' : 'Next →'}</button>
          </div>
        </div>
      </div>
    </div>
  )
}
